import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { HelpCircle } from 'lucide-react';
import { useAuth } from '@/AuthContext';
import { TOURS, AREA_BY_MODULE, AREA_LABEL, AUTO_TOUR_ROLES } from './tours';
import { Spotlight } from './Spotlight';
import './tour.css';

const API = process.env.REACT_APP_BACKEND_URL;

/** Mounts the ? button and runs the spotlight tour for the current area. */
export function TourLayer({ activeModule }) {
  const { user } = useAuth();
  const area = AREA_BY_MODULE[activeModule || 'dashboard'];
  const [prefs, setPrefs] = useState(null); // { hidden, seen }
  const [running, setRunning] = useState(null); // area being toured
  const autoFired = useRef({});

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/api/auth/me/tour`).then((r) => setPrefs(r.data)).catch(() => setPrefs({ hidden: false, seen: {} }));
  }, [user]);

  const save = useCallback((patch) => {
    setPrefs((p) => {
      const next = { hidden: patch.hidden ?? p?.hidden ?? false, seen: { ...(p?.seen || {}) } };
      Object.entries(patch.seen || {}).forEach(([k, v]) => { if (v) next.seen[k] = true; else delete next.seen[k]; });
      return next;
    });
    axios.put(`${API}/api/auth/me/tour`, patch).catch(() => {});
  }, []);

  const start = useCallback((which) => { if (TOURS[which]) setRunning(which); }, []);

  // Only offer the ? where the current area actually has something to point at (e.g. not on the editor queue)
  const [hasTargets, setHasTargets] = useState(false);
  useEffect(() => {
    if (!area) { setHasTargets(false); return; }
    const check = () => setHasTargets(TOURS[area].steps.some((s) => !s.target.includes('tour-help-button') && document.querySelector(s.target)));
    const t0 = setTimeout(check, 600);
    const id = setInterval(check, 2000);
    return () => { clearTimeout(t0); clearInterval(id); };
  }, [area, activeModule]);

  // First visit to an area → auto-start for agents / marketing managers (unless hidden forever)
  const eligible = !!user && AUTO_TOUR_ROLES.includes(user.role);
  useEffect(() => {
    if (!prefs || !area || !eligible || prefs.hidden || prefs.seen?.[area] || running || autoFired.current[area] || !hasTargets) return;
    autoFired.current[area] = true;
    const id = setTimeout(() => setRunning(area), 900);
    return () => clearTimeout(id);
  }, [prefs, area, eligible, running, hasTargets]);

  const finish = useCallback(({ hideForever = false } = {}) => {
    const which = running;
    setRunning(null);
    if (!which) return;
    save(hideForever ? { hidden: true, seen: { [which]: true } } : { seen: { [which]: true } });
  }, [running, save]);

  const tour = useMemo(() => (running ? TOURS[running] : null), [running]);

  if (!user) return null;

  return (
    <>
      {area && hasTargets && (
        <button type="button" className={`tour-help ${running ? 'is-touring' : ''}`} disabled={!!running} onClick={() => start(area)} title={`Show me around ${AREA_LABEL[area]}`} aria-label="Help tour" data-testid="tour-help-button">
          <HelpCircle size={22} />
        </button>
      )}
      {tour && <Spotlight tour={tour} onClose={finish} />}
    </>
  );
}
